/**
 * The rider surfaces' shared layout primitives.
 *
 * ChooseChow's cards are composed from `components/chow` and the ui-minimal
 * primitives, so the rider pages do the same and none of this is edition
 * styling — the teal accent rides the theme tokens. What does live here is the
 * handful of local arrangements that recur on every rider page: a card body, a
 * two-column title/amount row, and a caption-size fact row with an icon.
 *
 * These were previously declared twice, once per page file, and the copies
 * drifted. Keeping one block is what stops that: a change to the fact row's
 * spacing now lands everywhere by construction rather than by remembering.
 */
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'

export const riderStyle = {
  /** A vertical stack of cards. */
  List: styled.div`
    display: grid;
    gap: ${minimalVars.space.sm};
  `,
  /** The contents of one card. */
  Stack: styled.div`
    display: grid;
    gap: ${minimalVars.space.xs};
    min-width: 0;
  `,
  /** Title on the left, trailing control or amount on the right. */
  Top: styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
  `,
  Title: styled.h2`
    margin: 0;
    color: ${minimalVars.color.textPrimary};
    font-family: ${minimalVars.typography.displayFamily};
    /* 17.6px sits 9.1% from bodySize and 25% from h2Size, so no token matches. */
    font-size: 1.1rem;
    line-height: 1.2;
  `,
  /** Secondary line under a title: an address, a status, a hint. */
  Meta: styled.p`
    margin: 0;
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    line-height: 1.45;
  `,
  /** The section heading between card groups. */
  SectionTitle: styled.h2`
    margin: ${minimalVars.space.xs} 0 0;
    display: inline-flex;
    align-items: center;
    gap: ${minimalVars.space.xs};
    font-family: ${minimalVars.typography.displayFamily};
    /* 20px sits 10% from h2Size and 25% from bodySize, so no token matches. */
    font-size: 1.25rem;
    font-weight: ${minimalVars.typography.weightSemibold};
    color: ${minimalVars.color.textPrimary};
  `,
  /** Caption-size row of icon-and-value facts: distance, time, pay. */
  Facts: styled.div`
    display: flex;
    align-items: center;
    gap: ${minimalVars.space.sm};
    flex-wrap: wrap;
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.captionSize};
  `,
  Fact: styled.span`
    display: inline-flex;
    align-items: center;
    /* 5px is off the space scale, so this icon gap stays raw. */
    gap: 5px;
  `,
  /** The paired Skip / Accept row. */
  Actions: styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${minimalVars.space.xs};
    padding-top: ${minimalVars.space['2xs']};
  `,
  /** Controls that sit inside a panel's action slot, rather than in a card. */
  InlineActions: styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: ${minimalVars.space['2xs']};
  `,
  /** A single full-width action, when there is only one. */
  FullAction: styled.div`
    display: grid;
    padding-top: ${minimalVars.space['2xs']};
  `,
  /** A two-up row of stat cards. */
  StatRow: styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${minimalVars.space.xs};
  `,
  /** Wraps one grid cell so its entrance animation is staggered with siblings. */
  Cell: styled.div`
    min-width: 0;
    display: flex;
  `,
}
